<template>
    <StepShell :embedded="embedded">
        <StepHeader :align="embedded ? 'start' : 'center'" :size="embedded ? 'step' : 'display'" title="Perspective" :lede="gapCopy">
            <template #mark><PerspectiveMark /></template>
        </StepHeader>

        <NadiaCard :title="headlineCopy">
            <div>
                <p class="card-body">{{ PITCH_COPY }}</p>
                <!-- Describe the questions before the CTA so users know they are agreeing to an assessment.
                     -->
                <StepPoints :points="facts" variant="dot" />
            </div>
        </NadiaCard>

        <!-- The linear flow allows skipping the assessment to continue coaching without readings or a
             profile.
             -->
        <StepActions flush>
            <BaseButton v-if="!embedded" theme="tertiary" class="step-skip" @click="emit('skip')">Skip for now</BaseButton>
            <BaseButton theme="primary" @click="emit('continue')">
                {{ ctaLabel }}
            </BaseButton>
        </StepActions>
    </StepShell>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import PerspectiveMark from "~vue/icons/PerspectiveMark.vue";
import { computed } from "vue";

import NadiaCard from "./NadiaCard.vue";
import { formatHours } from "./scanStats";
import StepActions from "./StepActions.vue";
import StepHeader from "./StepHeader.vue";
import type { StepPoint } from "./StepPoints.vue";
import StepPoints from "./StepPoints.vue";
import StepShell from "./StepShell.vue";
import type { CalendarScanStats } from "./types";

const {
    stats,
    managesPeople,
    questionCount,
    calendarSkipped,
    embedded = false,
} = defineProps<{
    embedded?: boolean;
    stats: CalendarScanStats | null;
    managesPeople: boolean;
    questionCount: number;
    /** True when there is no calendar behind this screen, so nothing can be read back. */
    calendarSkipped: boolean;
}>();

const emit = defineEmits<{ continue: []; skip: [] }>();

/* Use the server's Perspectives question count so the CTA stays accurate when the question bank changes. */
const ctaLabel = computed(() => `Start the ${questionCount} questions`);

const PITCH_COPY = "Take your Perspective to see which of 16 working styles fits you, and what it says about the way you work. It takes about ten minutes.";

/* Without a calendar there is no "week" to have seen, so the headline cannot
   claim one. These questions are then the only thing the flow has to go on. */
const headlineCopy = computed(() => {
    if (!calendarSkipped) {
        return "I can see your week. I can’t see you yet.";
    }
    return embedded ? "Let’s start with how you work" : "Then let’s start with how you work";
});

const facts = computed<StepPoint[]>(() => [
    { tone: "pink", lead: "Nothing to write — slide between two ways of working" },
    { tone: "amber", lead: "Answers save as you go, so you can stop whenever you like" },
    calendarSkipped
        ? { tone: "teal", lead: embedded ? "Then I use your answers in every conversation from here on" : "Then I know how you work before you ask me anything" }
        : { tone: "teal", lead: "Then I read your calendar again, and say what those meetings cost you" },
]);

/* The argument for the questions, so it sits immediately above them. */
const gapCopy = computed(() => {
    if (calendarSkipped) {
        const work = managesPeople ? "you and your team work" : "you work";
        if (embedded) {
            return `These ${questionCount} questions tell me how ${work} — the part of you that no calendar or org chart can show.`;
        }
        return `No calendar, so these ${questionCount} questions are what I have. They tell me how ${work} — enough to coach you properly from your first conversation.`;
    }
    const week = managesPeople ? "your team’s week" : "your week";
    const load = stats !== null && stats.hours_per_week > 0 ? ` — ${formatHours(stats.hours_per_week)} hours in meetings` : "";
    return `Your calendar shows the shape of ${week}${load}. It can’t show whether that works for you or wears you down.`;
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";
@layer components {
    .card-body {
        @apply body-small text-ink-muted;
    }
}
</style>
